import React, { useState } from "react";
import { Tabs, Tab, Box, Typography } from "@mui/material";
import CustomTooltip from "./CustomTooltip";
import { useTranslation } from "react-i18next";

interface TabItem {
  id: string | number;
  label: string;
  icon?: JSX.Element;
  // Rendered instead of `icon` while this tab is selected — an escape hatch
  // for icons whose colors can't be cleanly overridden via CSS. Falls back
  // to `icon` when not provided.
  activeIcon?: JSX.Element;
  tooltip?: string;
}

interface CustomTabsProps {
  tabs: TabItem[];
  activeTab?: string | number;
  onTabChange?: (val: number | string) => void;
  isRequireFullWidth?: boolean;
  // "segmented" is the sub-tab style: lavender track, active tab as a white pill.
  variant?: "default" | "segmented";
}

const getContentColor = (isActive: boolean) => (isActive ? "#5921F2" : "#6A7282");

const getTabSx = (isActive: boolean) => ({
  textTransform: "none" as const,
  minHeight: "45px",
  height: "45px",
  padding: "13px 24px",
  borderRadius: "8px 8px 0 0",
  ...(isActive && {
    backgroundColor: "rgba(89, 33, 242, 0.15)",
    borderBottom: "3px solid #5921F2",
  }),
});

const getFontWeight = (isActive: boolean, isSegmented: boolean) => {
  if (!isActive) return 400;
  return isSegmented ? 600 : 700;
};

const getSegmentedTabSx =(isActive: boolean) => ({
  textTransform: "none" as const,
  minHeight: "auto",
  minWidth: 0,
  padding: "8px 16px",
  borderRadius: "8px",
  ...(isActive && {
    backgroundColor: "#FFFFFF",
    boxShadow: "0 1px 4px 0 rgba(89, 33, 242, 0.12)",
  }),
});

const containerSx = {
  backgroundColor: "custom.background.primary",
  width: "100%",
  boxSizing: "border-box",
  py: 1,
  px: 2,
};

const segmentedContainerSx = {
  display: "inline-flex",
  backgroundColor: "rgba(89, 33, 242, 0.15)",
  borderRadius: "12px",
  p: 0.5,
};

const CustomTabs: React.FC<CustomTabsProps> = ({
  tabs,
  activeTab: externalActiveTab,
  onTabChange,
  isRequireFullWidth,
  variant = "default",
}) => {
  const { t } = useTranslation();
  const isSegmented = variant === "segmented";

  const [internalActiveTab, setInternalActiveTab] = useState(tabs?.[0]?.id);

  const activeTab = externalActiveTab ?? internalActiveTab;

  return (
    <Box sx={isSegmented ? segmentedContainerSx : containerSx}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Tabs
          value={activeTab}
          variant={isRequireFullWidth ? "fullWidth" : "standard"}
          sx={isSegmented ? { minHeight: "auto" } : { width: "100%" }}
          onChange={(_, newValue) => {
            setInternalActiveTab(newValue);
            onTabChange?.(newValue);
          }}
          slotProps={{
            indicator: { style: { display: "none" } },
          }}
        >
          {tabs.map((tab) => {
            const isActive = activeTab === tab?.id;
            const contentColor = getContentColor(isActive);
            const iconToRender = isActive ? (tab.activeIcon ?? tab.icon) : tab.icon;

            return (
              <Tab
                key={tab.id}
                id={tab.id.toString()}
                value={tab.id}
                label={
                  <CustomTooltip
                    title={t(tab?.tooltip ?? "")}
                    placement="right"
                  >
                    <Box display="flex" alignItems="center" gap={isSegmented ? "8px" : "4px"}>
                      {iconToRender && (
                        <Box
                          display="flex"
                          component="span"
                          alignItems="center"
                          sx={{
                            color: contentColor,
                            // Some icons set their color on <svg> itself, not on the paths.
                            "& svg[stroke]:not([stroke='none']), & svg [stroke]:not([stroke='none'])": {
                              stroke: "currentColor",
                            },
                            "& svg[fill]:not([fill='none']), & svg [fill]:not([fill='none'])": {
                              fill: "currentColor",
                            },
                          }}
                        >
                          {React.cloneElement(iconToRender, { width: "18px", height: "18px" })}
                        </Box>
                      )}
                      <Typography
                        variant="body1"
                        sx={{
                          color: contentColor,
                          fontWeight: getFontWeight(isActive, isSegmented),
                          fontSize: "14px",
                          fontFamily: "Inter",
                          lineHeight: isSegmented ? "20px" : "normal",
                          ...(isSegmented && { letterSpacing: 0, textAlign: "center" }),
                        }}
                      >
                        {t(tab.label)}
                      </Typography>
                    </Box>
                  </CustomTooltip>
                }
                sx={isSegmented ? getSegmentedTabSx(isActive) : getTabSx(isActive)}
              />
            );
          })}
        </Tabs>
      </Box>
    </Box>
  );
};

export default CustomTabs;
